import clsx from 'clsx';
import {
  type ChangeEvent,
  type KeyboardEvent,
  forwardRef,
  useRef,
} from 'react';

import { MagnifyingGlassIcon } from '@dynatrace/strato-icons';

import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useControllableState } from '../../core/hooks/useControllableState.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { useSlots } from '../../core/hooks/useSlots.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import { DataTestId } from '../../core/types/data-props.js';
import type { FormControlProps } from '../../core/types/form-control-props.js';
import type { FormControlStateProps } from '../../core/types/form-control-state.js';
import { MaskingProps } from '../../core/types/masking-props.js';
import { StylingProps } from '../../core/types/styling-props.js';
import { WithChildren } from '../../core/types/with-children.js';
import { mergeProps } from '../../core/utils/merge-props.js';
import type { BaseInputProps } from '../_base-input/base-input.types.js';
import { BaseInput } from '../_base-input/BaseInput.js';
import { BaseInputButton } from '../_base-input/BaseInputButton.js';
import { BaseInputSuffix } from '../_base-input/BaseInputContent.js';
import { ClearButton } from '../_base-input/ClearButton.js';
import { useInternalFormRegistration } from '../form/hooks/useInternalFormRegistration.js';
import type { FormControlRef } from '../shared-types.js';
import type { MessageTarget } from '../validation/message-routing.js';
import * as styles from './SearchInput.sty.js';
import { Stepper } from './Stepper.js';

const COMPONENT_NAME = 'SearchInput';

//#region Typings
/**
 * The properties of the SearchInput component.
 * @public
 */
export type SearchInputProps = Pick<
  BaseInputProps,
  | 'variant'
  | 'onBlur'
  | 'autoComplete'
  | 'placeholder'
  | 'minLength'
  | 'maxLength'
> &
  // oxlint-disable-next-line typescript/no-deprecated -- APPDEV-18321
  FormControlStateProps &
  WithChildren &
  StylingProps &
  DataTestId &
  MaskingProps &
  FormControlProps<string, (value: string) => void, HTMLInputElement> &
  BehaviorTrackingProps & {
    /**
     * Internal: associates this control with a `_Form` rendered outside its
     * React subtree via the native `form` attribute. Unstable.
     * @internal
     */
    _form?: string;
    /**
     * Internal: routes this control's native/Strato validation messages to the
     * field outlet (default), the enclosing form's outlet, or both. Unstable.
     * @internal
     */
    _messageTarget?: MessageTarget | MessageTarget[];
  };

//#endregion Typings

const SearchInputRoot = /* @__PURE__ */ forwardRef<
  FormControlRef<HTMLDivElement>,
  SearchInputProps
>((props, forwardedRef) => {
  const {
    children,
    onChange,
    id: propId,
    name,
    _form,
    _messageTarget,
    value,
    defaultValue,
    placeholder,
    required,
    readOnly = false,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'aria-disabled': ariaDisabledProp,
    className: consumerClassName,
    onValidityChange,
    ...remainingProps
  }: SearchInputProps = props;

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);

  const [internalValue, setInternalValue] = useControllableState({
    defaultValue,
    value,
    onChange,
  });

  const controlRef = useRef<FormControlRef<HTMLDivElement>>(null);
  const mergedRef = useMergeRefs<FormControlRef<HTMLDivElement> | null>([
    forwardedRef,
    controlRef,
  ]);

  const formControl = useInternalFormRegistration({
    id: propId,
    name,
    form: _form,
    messageTarget: _messageTarget,
    value: internalValue ?? '',
    controlRef,
    onValidityChange,
  });

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInternalValue(e.target.value);
    if (slots.stepper && e.target.value === '') {
      const { onCurrentChange } = slots.stepper.props;
      onCurrentChange(0);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!slots.stepper) {
      return;
    }
    if (e.key !== 'Enter') {
      return;
    }

    e.preventDefault();
    const { current, total, onCurrentChange } = slots.stepper.props;

    if (e.shiftKey) {
      onCurrentChange(current > 0 ? current - 1 : total - 1);
    } else {
      onCurrentChange(current < total - 1 ? current + 1 : 0);
    }
  };

  const onClear = () => {
    setInternalValue('');
  };

  const [slots] = useSlots(children, {
    stepper: Stepper,
  });

  const isEmpty = internalValue === '' || internalValue === undefined;
  const hasStepper = !!slots.stepper;

  return (
    <BaseInput
      ref={mergedRef}
      {...mergeProps(restProps, behavioralTrackingProps)}
      {...(hasStepper ? { onKeyDown: handleKeyDown } : {})}
      id={formControl.id}
      name={name}
      form={_form}
      className={clsx(styles.searchInput({ hasStepper }), consumerClassName)}
      type="search"
      value={internalValue}
      required={required}
      readOnly={readOnly}
      onChange={handleOnChange}
      onValidityChange={formControl.onValidityChange}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      aria-disabled={ariaDisabledProp}
      placeholder={placeholder}
      data-dt-component={COMPONENT_NAME}
      // The width of the input is handled via CSS and the parent elements,
      // so we set it to the minimum here to prevent the default width behavior of inputs
      size={1}
    >
      <BaseInput.Prefix>
        <MagnifyingGlassIcon />
      </BaseInput.Prefix>
      {!isEmpty && (
        <BaseInput.ReservedSuffix>
          {slots.stepper}
          <ClearButton onClear={onClear} />
        </BaseInput.ReservedSuffix>
      )}
      {children}
    </BaseInput>
  );
});

/**
 * Use the `SearchInput` component for search queries.
 * It includes a built-in `Clear` button for quick resets.
 * @public
 */
export const SearchInput = Object.assign(SearchInputRoot, {
  Suffix: BaseInputSuffix,
  Button: BaseInputButton,
  Stepper,
});

(SearchInput as typeof SearchInput & { displayName: string }).displayName =
  COMPONENT_NAME;
